import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { WizardShell } from "../../components/Layouts/WizardShell";
import {
  useGetAvailabilityQuery,
  useGetPropertyByCodeQuery,
} from "../../store/services/publicApi";
import {
  AvailabilityLayout,
  EmptyState,
  FloorplanList,
} from "./Availability.style";
import { FilterBar } from "./components/FilterBar";
import { FloorplanRow } from "./components/FloorplanRow";
import { MoveInDateModal } from "./components/MoveInDateModal";
import { useAvailabilityFilters } from "./useAvailabilityFilters";
import { AvailabilityResponse, UnitResult } from "./types";
import { getVisibleFloorplans } from "./availabilityRows";
import { buildAvailabilityUnitPath } from "./availabilityReuse";

export const AvailabilityScreen: React.FC = () => {
  const { propertyCode } = useParams<{ propertyCode: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { filters, setFilter, clearFacets } = useAvailabilityFilters();

  const [modalOpen, setModalOpen] = useState<boolean>(
    !filters.moveInDate ||
      !new URLSearchParams(window.location.search).get("moveInDate")
  );

  const queryArgs = useMemo(
    () => ({
      propertyCode: propertyCode || "",
      moveInDate: filters.moveInDate,
      bedrooms: filters.bedrooms,
      floorLevel: filters.floorLevel,
      petsPermitted: filters.petsPermitted ?? undefined,
      rentMin: filters.rentMin ?? undefined,
      rentMax: filters.rentMax ?? undefined,
      sqftMin: filters.sqftMin ?? undefined,
      sqftMax: filters.sqftMax ?? undefined,
      unitNumber: filters.unitNumber ?? undefined,
      perPage: 200,
    }),
    [propertyCode, filters]
  );

  const { data: propertyData } = useGetPropertyByCodeQuery(propertyCode || "", {
    skip: !propertyCode,
  }) as {
    data: { id: string; name: string; abbreviation: string } | undefined;
  };

  const { data, isLoading, isFetching, error } = useGetAvailabilityQuery(
    queryArgs,
    {
      skip: !propertyCode || !filters.moveInDate || modalOpen,
    }
  ) as {
    data: AvailabilityResponse | undefined;
    isLoading: boolean;
    isFetching: boolean;
    error: any;
  };

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("moveInDate")) {
      setModalOpen(false);
    }
  }, []);

  const unitsByFloorplan = useMemo(() => {
    const map = new Map<string, UnitResult[]>();
    if (!data) return map;
    for (const u of data.units) {
      const list = map.get(u.unitType) ?? [];
      list.push(u);
      map.set(u.unitType, list);
    }
    return map;
  }, [data]);

  const onApply = (unit: UnitResult) => {
    navigate(
      buildAvailabilityUnitPath({
        propertyCode: propertyCode || "",
        unitNumber: unit.unitNumber,
        moveInDate: filters.moveInDate,
        searchParams,
      })
    );
  };

  return (
    <WizardShell maxWidth={1400}>
      <MoveInDateModal
        open={modalOpen}
        propertyName={
          data?.property.name || propertyData?.name || propertyCode || ""
        }
        initialDate={filters.moveInDate}
        onClose={(newDate) => {
          setFilter("moveInDate", newDate);
          setModalOpen(false);
        }}
      />

      {(isLoading || !data) && !error && (
        <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <EmptyState sx={{ m: 3 }}>
          {error?.status === 404 || error?.originalStatus === 404
            ? "Property not found."
            : "Something went wrong loading availability."}
        </EmptyState>
      )}

      {data && !modalOpen && (
        <AvailabilityLayout>
          <Typography variant="h4" noWrap>
            {data?.property.name || propertyData?.name || "Availability"}
          </Typography>
          <FilterBar
            filters={filters}
            bounds={data.bounds}
            facets={{
              bedrooms: data.facets.bedrooms,
              unitType: data.facets.unitType,
              floorLevel: data.facets.floorLevel,
            }}
            onChange={setFilter}
            onClear={clearFacets}
            onChangeMoveIn={() => setModalOpen(true)}
          />

          {isFetching && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block" }}
            >
              Updating...
            </Typography>
          )}

          {(() => {
            const visible = getVisibleFloorplans(
              data.floorplans,
              unitsByFloorplan,
              filters.floorplans
            );
            if (visible.length === 0) {
              return (
                <EmptyState>
                  <Typography sx={{ mb: 2 }}>
                    No available units match {filters.moveInDate}
                    {filters.unitNumber
                      ? ` for unit ${filters.unitNumber}`
                      : ""}
                    . Try a different move-in date.
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setModalOpen(true)}
                  >
                    Change move-in date
                  </Button>
                </EmptyState>
              );
            }
            return (
              <FloorplanList>
                {visible.map((fp) => (
                  <FloorplanRow
                    key={fp.unitType}
                    floorplan={fp}
                    units={unitsByFloorplan.get(fp.unitType) ?? []}
                    expanded={filters.floorplans.includes(fp.unitType)}
                    onToggle={() =>
                      setFilter(
                        "floorplans",
                        filters.floorplans.includes(fp.unitType)
                          ? filters.floorplans.filter((k) => k !== fp.unitType)
                          : [...filters.floorplans, fp.unitType]
                      )
                    }
                    onApply={onApply}
                  />
                ))}
              </FloorplanList>
            );
          })()}
        </AvailabilityLayout>
      )}
    </WizardShell>
  );
};
